import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Mail, Loader2, X, Printer, ExternalLink, Receipt, Store, Calendar, CreditCard, Phone, MapPin, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFullyKiosk } from "@/hooks/useFullyKiosk";

interface ReceiptData {
  order: {
    id: string;
    customerNote: string | null;
    paymentMethod: string;
    paymentStatus: string;
    subtotal: string;
    tax: string;
    tip: string;
    total: string;
    status: string;
    createdAt: string;
  };
  items: Array<{
    id: string;
    name: string;
    price: string;
    quantity: number;
    customerNote: string | null;
    modifiers?: Array<{
      name: string;
      price: string;
    }>;
  }>;
  restaurant: {
    id: string;
    name: string;
    address: string | null;
    city: string | null;
    country: string | null;
    phone: string | null;
  };
  table: {
    tableNumber: string;
  } | null;
}

interface ReceiptDialogProps {
  orderId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReceiptDialog({ orderId, open, onOpenChange }: ReceiptDialogProps) {
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [email, setEmail] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const receiptRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { t, formatCurrency } = useLanguage();
  const { isFully, printHtml } = useFullyKiosk();

  const { data: receiptData, isLoading, error } = useQuery<ReceiptData>({
    queryKey: ['/api/orders', orderId, 'receipt'],
    enabled: !!orderId && open,
  });

  const handleSendEmail = async () => {
    if (!email || !orderId) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: t('invalidEmail'),
        description: t('pleaseEnterValidEmail'),
        variant: "destructive",
      });
      return;
    }

    setIsSendingEmail(true);
    try {
      const response = await apiRequest("POST", `/api/orders/${orderId}/email-receipt`, {
        email,
      });

      if (response.ok) {
        toast({
          title: t('receiptSent'),
          description: `${t('receiptSentTo')} ${email}`,
        });
        setShowEmailInput(false);
        setEmail("");
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to send email");
      }
    } catch (error) {
      console.error("Error sending receipt email:", error);
      toast({
        title: t('failedToSend'),
        description: error instanceof Error ? error.message : t('couldNotSendEmail'),
        variant: "destructive",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Print function that clones content to a print container
  const handlePrint = async () => {
    if (!receiptRef.current || !receiptData) return;

    setIsPrinting(true);

    try {
      // Attempt to trigger a backend print job (for the silent bridge)
      const response = await apiRequest("POST", `/api/orders/${orderId}/print`);

      if (response.ok) {
        const result = await response.json();
        if (result.notifiedBridge) {
          toast({
            title: t('printing'),
            description: "Sent to silent thermal printer",
          });
          setTimeout(() => setIsPrinting(false), 1000);
          return;
        }
      }
    } catch (error) {
      console.warn("Backend print failed, falling back to browser print:", error);
    }

    // Try Fully Kiosk silent printing as second option
    if (isFully) {
      // ... (existing Fully Kiosk logic)
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Receipt - ${receiptData.restaurant.name}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; max-width: 400px; margin: 0 auto; color: #1a1a1a; font-size: 14px; line-height: 1.5; background: white; }
            .header { text-align: center; padding-bottom: 16px; border-bottom: 2px dashed #e5e5e5; margin-bottom: 16px; }
            .header h1 { font-size: 20px; font-weight: 700; margin-bottom: 4px; }
            .header p { color: #666; font-size: 12px; }
            .order-info { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
            .info-box { background: #f5f5f5; padding: 10px; border-radius: 8px; }
            .info-box label { font-size: 10px; text-transform: uppercase; color: #888; letter-spacing: 0.5px; }
            .info-box p { font-weight: 600; font-size: 13px; }
            .items { border-top: 1px solid #e5e5e5; border-bottom: 1px solid #e5e5e5; padding: 16px 0; margin-bottom: 16px; }
            .items h2 { font-size: 14px; font-weight: 600; margin-bottom: 12px; }
            .item { display: flex; justify-content: space-between; margin-bottom: 8px; }
            .item-qty { background: #e0e7ff; color: #4338ca; padding: 2px 6px; border-radius: 4px; font-size: 11px; font-weight: 600; margin-right: 8px; }
            .item-price { font-weight: 600; min-width: 60px; text-align: right; }
            .totals { margin-bottom: 16px; }
            .total-row { display: flex; justify-content: space-between; margin-bottom: 6px; font-size: 13px; }
            .total-row.final { border-top: 2px solid #1a1a1a; padding-top: 10px; margin-top: 10px; font-size: 18px; font-weight: 700; }
            .footer { text-align: center; padding-top: 16px; border-top: 2px dashed #e5e5e5; }
            .badge { display: inline-block; background: #e5e5e5; padding: 3px 8px; border-radius: 12px; font-size: 11px; }
            .badge.paid { background: #dcfce7; color: #166534; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${receiptData.restaurant.name}</h1>
            ${receiptData.restaurant.address ? `<p>${receiptData.restaurant.address}${receiptData.restaurant.city ? `, ${receiptData.restaurant.city}` : ''}</p>` : ''}
          </div>
          <div class="order-info">
            <div class="info-box">
              <label>Order #</label>
              <p>${receiptData.order.id.slice(-8).toUpperCase()}</p>
            </div>
            <div class="info-box">
              <label>Date & Time</label>
              <p>${format(new Date(receiptData.order.createdAt), "MMM d, yyyy h:mm a")}</p>
            </div>
          </div>
          <div class="items">
            ${receiptData.items.map(item => `
              <div class="item">
                <span>${item.quantity}x ${item.name}</span>
                <span class="item-price">${formatCurrency(parseFloat(item.price) * item.quantity)}</span>
              </div>
            `).join('')}
          </div>
          <div class="totals">
            <div class="total-row"><span>Subtotal</span><span>${formatCurrency(parseFloat(receiptData.order.subtotal))}</span></div>
            <div class="total-row final"><span>Total</span><span>${formatCurrency(parseFloat(receiptData.order.total))}</span></div>
          </div>
          <div class="footer"><p>Thank you!</p></div>
        </body>
        </html>
      `;

      const success = printHtml(printContent);
      if (success) {
        toast({
          title: t('printing'),
          description: "Sent to printer via Fully Kiosk",
        });
        setIsPrinting(false);
        return;
      }
    }

    // Default window.open fallback for non-Fully Kiosk browsers
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (!printWindow) {
      toast({
        title: "Print blocked",
        description: "Please allow popups to print the receipt.",
        variant: "destructive",
      });
      setIsPrinting(false);
      return;
    }

    // Build print content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${receiptData.restaurant.name}</title>
        <style>
          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            padding: 20px;
            max-width: 400px;
            margin: 0 auto;
            color: #1a1a1a;
            font-size: 14px;
            line-height: 1.5;
          }
          .header {
            text-align: center;
            padding-bottom: 16px;
            border-bottom: 2px dashed #e5e5e5;
            margin-bottom: 16px;
          }
          .header h1 {
            font-size: 20px;
            font-weight: 700;
            margin-bottom: 4px;
          }
          .header p {
            color: #666;
            font-size: 12px;
          }
          .order-info {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin-bottom: 16px;
          }
          .info-box {
            background: #f5f5f5;
            padding: 10px;
            border-radius: 8px;
          }
          .info-box label {
            font-size: 10px;
            text-transform: uppercase;
            color: #888;
            letter-spacing: 0.5px;
          }
          .info-box p {
            font-weight: 600;
            font-size: 13px;
          }
          .items {
            border-top: 1px solid #e5e5e5;
            border-bottom: 1px solid #e5e5e5;
            padding: 16px 0;
            margin-bottom: 16px;
          }
          .items h2 {
            font-size: 14px;
            font-weight: 600;
            margin-bottom: 12px;
          }
          .item {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
          }
          .item-name {
            flex: 1;
          }
          .item-qty {
            background: #e0e7ff;
            color: #4338ca;
            padding: 2px 6px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 600;
            margin-right: 8px;
          }
          .item-price {
            font-weight: 600;
            min-width: 60px;
            text-align: right;
          }
          .item-note {
            font-size: 11px;
            color: #888;
            font-style: italic;
            margin-left: 36px;
          }
          .totals {
            margin-bottom: 16px;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 6px;
            font-size: 13px;
          }
          .total-row.final {
            border-top: 2px solid #1a1a1a;
            padding-top: 10px;
            margin-top: 10px;
            font-size: 18px;
            font-weight: 700;
          }
          .total-row span:first-child {
            color: #666;
          }
          .total-row.final span:first-child {
            color: #1a1a1a;
          }
          .footer {
            text-align: center;
            padding-top: 16px;
            border-top: 2px dashed #e5e5e5;
          }
          .footer p {
            font-size: 11px;
            color: #888;
          }
          .badge {
            display: inline-block;
            background: #e5e5e5;
            padding: 3px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 500;
          }
          .badge.paid {
            background: #dcfce7;
            color: #166534;
          }
          .badge.pending {
            background: #fef9c3;
            color: #854d0e;
          }
          @media print {
            body {
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${receiptData.restaurant.name}</h1>
          ${receiptData.restaurant.address ? `<p>${receiptData.restaurant.address}${receiptData.restaurant.city ? `, ${receiptData.restaurant.city}` : ''}</p>` : ''}
          ${receiptData.restaurant.phone ? `<p>${receiptData.restaurant.phone}</p>` : ''}
        </div>
        
        <div class="order-info">
          <div class="info-box">
            <label>Order #</label>
            <p>${receiptData.order.id.slice(-8).toUpperCase()}</p>
          </div>
          <div class="info-box">
            <label>Date & Time</label>
            <p>${format(new Date(receiptData.order.createdAt), "MMM d, yyyy")}<br/><span style="color:#666;font-weight:400">${format(new Date(receiptData.order.createdAt), "h:mm a")}</span></p>
          </div>
          ${receiptData.table ? `
          <div class="info-box">
            <label>Table</label>
            <p>${receiptData.table.tableNumber}</p>
          </div>
          ` : ''}
          <div class="info-box">
            <label>Payment</label>
            <p>
              <span class="badge">${receiptData.order.paymentMethod === 'card' ? 'Card' : receiptData.order.paymentMethod === 'cash' ? 'Cash' : receiptData.order.paymentMethod}</span>
              ${receiptData.order.paymentStatus === 'completed' ? '<span class="badge paid">Paid</span>' : '<span class="badge pending">Pending</span>'}
            </p>
          </div>
        </div>
        
        <div class="items">
          <h2>Order Items</h2>
          ${receiptData.items.map(item => `
            <div>
              <div class="item">
                <div class="item-name">
                  <span class="item-qty">${item.quantity}x</span>
                  ${item.name}
                </div>
                <span class="item-price">${formatCurrency(parseFloat(item.price) * item.quantity)}</span>
              </div>
              ${item.customerNote ? `<p class="item-note">"${item.customerNote}"</p>` : ''}
            </div>
          `).join('')}
        </div>
        
        <div class="totals">
          <div class="total-row">
            <span>Subtotal</span>
            <span>${formatCurrency(parseFloat(receiptData.order.subtotal))}</span>
          </div>
          <div class="total-row">
            <span>Tax</span>
            <span>${formatCurrency(parseFloat(receiptData.order.tax))}</span>
          </div>
          ${parseFloat(receiptData.order.tip) > 0 ? `
          <div class="total-row">
            <span>Tip</span>
            <span>${formatCurrency(parseFloat(receiptData.order.tip))}</span>
          </div>
          ` : ''}
          <div class="total-row final">
            <span>Total</span>
            <span>${formatCurrency(parseFloat(receiptData.order.total))}</span>
          </div>
        </div>
        
        ${receiptData.order.customerNote ? `
        <div style="background:#f5f5f5;padding:10px;border-radius:8px;margin-bottom:16px;">
          <p style="font-size:10px;text-transform:uppercase;color:#888;margin-bottom:4px;">Special Instructions</p>
          <p style="font-style:italic;font-size:13px;">"${receiptData.order.customerNote}"</p>
        </div>
        ` : ''}
        
        <div class="footer">
          <p>Thank you for your order!</p>
          <p style="margin-top:8px;">Powered by Waiterix.com</p>
        </div>
        
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();

    setIsPrinting(false);
  };

  if (!orderId) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden bg-gradient-to-b from-background to-muted/30 flex flex-col max-h-[96vh] sm:max-h-[90vh]" data-testid="dialog-receipt">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16 px-8">
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-primary/10 animate-pulse" />
              <Loader2 className="h-8 w-8 animate-spin text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-testid="loader-receipt" />
            </div>
            <p className="mt-4 text-muted-foreground">Loading your receipt...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 px-8">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
              <X className="h-8 w-8 text-destructive" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Receipt Not Found</h3>
            <p className="text-muted-foreground text-center text-sm">
              We couldn't load this receipt. Please try again.
            </p>
            <Button variant="outline" className="mt-4" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        ) : receiptData ? (
          <>
            {/* Success Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6 text-center shrink-0">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-white/20 backdrop-blur rounded-full mb-3">
                <CheckCircle2 className="h-7 w-7" data-testid="icon-success" />
              </div>
              <h2 className="text-2xl font-bold mb-1">
                {receiptData.order.paymentMethod === 'cash' ? t('invoice') : t('receipt')}
              </h2>
              <p className="text-white/80 text-sm">{t('thankYouForYourOrder')}</p>
            </div>

            {/* Receipt Content */}
            <div ref={receiptRef} className="p-6 space-y-5 flex-1 overflow-y-auto">

              {/* Restaurant Info */}
              <div className="text-center pb-4 border-b border-dashed">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Store className="h-5 w-5 text-primary" />
                  <h3 className="text-xl font-bold">{receiptData.restaurant.name}</h3>
                </div>
                {receiptData.restaurant.address && (
                  <p className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {receiptData.restaurant.address}
                    {receiptData.restaurant.city && `, ${receiptData.restaurant.city}`}
                  </p>
                )}
                {receiptData.restaurant.phone && (
                  <p className="text-sm text-muted-foreground flex items-center justify-center gap-1 mt-1">
                    <Phone className="h-3 w-3" />
                    {receiptData.restaurant.phone}
                  </p>
                )}
              </div>

              {/* Order Details Grid */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-muted-foreground text-xs uppercase tracking-wide mb-1">{t('orderNumber')}</p>
                  <p className="font-mono font-bold text-primary" data-testid="text-order-number">
                    #{receiptData.order.id.slice(-8).toUpperCase()}
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-muted-foreground text-xs uppercase tracking-wide mb-1 flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {t('dateAndTime')}
                  </p>
                  <p className="font-medium text-xs" data-testid="text-order-date">
                    {format(new Date(receiptData.order.createdAt), "MMM d, yyyy")}
                    <br />
                    <span className="text-muted-foreground">{format(new Date(receiptData.order.createdAt), "h:mm a")}</span>
                  </p>
                </div>
                {receiptData.table && (
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-muted-foreground text-xs uppercase tracking-wide mb-1">{t('table')}</p>
                    <p className="font-bold" data-testid="text-table-number">{receiptData.table.tableNumber}</p>
                  </div>
                )}
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-muted-foreground text-xs uppercase tracking-wide mb-1 flex items-center gap-1">
                    <CreditCard className="h-3 w-3" />
                    {t('paymentMethod')}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary" className="text-xs" data-testid="badge-payment-method">
                      {receiptData.order.paymentMethod === 'card' ? t('card') : (receiptData.order.paymentMethod === 'cash' ? t('cash') : receiptData.order.paymentMethod)}
                    </Badge>
                    {receiptData.order.paymentStatus === 'completed' && (
                      <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs">
                        {t('paid')}
                      </Badge>
                    )}
                    {receiptData.order.paymentStatus === 'pending' && (
                      <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 text-xs">
                        {t('pending')}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Items List */}
              <div className="border-t border-b py-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Receipt className="h-4 w-4 text-primary" />
                  {t('orderItems')}
                </h4>
                <div className="space-y-3">
                  {receiptData.items.map((item) => (
                    <div key={item.id} className="flex justify-between items-start" data-testid={`item-${item.id}`}>
                      <div className="flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className="bg-primary/10 text-primary text-xs font-bold px-2 py-0.5 rounded">
                            {item.quantity}x
                          </span>
                          <span className="font-medium">{item.name}</span>
                        </div>
                        {item.modifiers && item.modifiers.length > 0 && (
                          <div className="text-xs text-muted-foreground ml-8 mt-1 space-y-0.5">
                            {item.modifiers.map((mod, idx) => (
                              <div key={idx}>
                                + {mod.name} {parseFloat(mod.price) > 0 && `(+${formatCurrency(parseFloat(mod.price))})`}
                              </div>
                            ))}
                          </div>
                        )}
                        {item.customerNote && (
                          <p className="text-xs text-muted-foreground italic ml-8 mt-1">
                            "{item.customerNote}"
                          </p>
                        )}
                      </div>
                      <span className="font-semibold text-right ml-4" data-testid={`item-price-${item.id}`}>
                        {formatCurrency(parseFloat(item.price) * item.quantity)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('subtotal')}</span>
                  <span data-testid="text-subtotal">{formatCurrency(parseFloat(receiptData.order.subtotal))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('tax')}</span>
                  <span data-testid="text-tax">{formatCurrency(parseFloat(receiptData.order.tax))}</span>
                </div>
                {parseFloat(receiptData.order.tip) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Sparkles className="h-3 w-3" /> Tip
                    </span>
                    <span data-testid="text-tip">{formatCurrency(parseFloat(receiptData.order.tip))}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-3 border-t-2 border-primary/20">
                  <span className="text-lg font-bold">{t('total')}</span>
                  <span className="text-xl font-bold text-primary" data-testid="text-total">
                    {formatCurrency(parseFloat(receiptData.order.total))}
                  </span>
                </div>
              </div>

              {/* Customer Note */}
              {receiptData.order.customerNote && (
                <div className="bg-muted/50 rounded-lg p-3 text-sm">
                  <p className="text-muted-foreground text-xs uppercase tracking-wide mb-1">{t('specialInstructions')}</p>
                  <p className="italic">"{receiptData.order.customerNote}"</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="p-4 pb-8 sm:pb-4 bg-muted/30 border-t space-y-3 shrink-0">
              {/* Email Input */}
              {showEmailInput ? (
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendEmail()}
                    className="flex-1"
                    data-testid="input-email"
                  />
                  <Button
                    onClick={handleSendEmail}
                    disabled={isSendingEmail}
                    data-testid="button-send-email"
                  >
                    {isSendingEmail ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowEmailInput(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowEmailInput(true)}
                    data-testid="button-email-receipt"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    {t('emailReceipt')}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    disabled={isPrinting}
                    onClick={handlePrint}
                    data-testid="button-print-receipt"
                  >
                    {isPrinting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Printer className="h-4 w-4 mr-2" />
                    )}
                    {t('printSavePDF')}
                  </Button>
                </div>
              )}

              {/* Track Order & Close */}
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  onClick={() => {
                    toast({
                      title: t('openingOrderTracking'),
                      description: t('orderTrackingWillOpen'),
                    });
                    window.open(`/track/${orderId}`, '_blank');
                  }}
                  data-testid="button-track-order"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {t('trackYourOrder')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  data-testid="button-close-receipt"
                >
                  {t('close')}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-8">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Receipt className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">{t('receiptNotFound')}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
